// TosAcceptanceGate.js
//
// Gate de onboarding que fuerza a un usuario logueado a ACEPTAR los Términos de
// Uso (click-through) antes de operar la app. Sigue el patrón de
// ProfileCompletionGate.js, pero en vez de redirigir a una ruta, RENDEREA una
// pantalla bloqueante (overlay full-screen) por encima de todo: el usuario no
// puede interactuar con nada hasta tildar el checkbox y aceptar.
//
// Criterio de "necesita aceptar" (viene calculado del backend):
//   - Logueado.
//   - NO superuser (los admins nunca quedan atrapados — mismo criterio que
//     ProfileCompletionGate).
//   - user.tos_needs_acceptance === true, es decir
//     tos_accepted_version !== settings.CURRENT_TOS_VERSION. Así, cuando se
//     publique una nueva versión de los términos, TODOS re-aceptan.
//
// Al aceptar: POST /api/user/accept-tos/ (setea versión + timestamp en el user)
// → refreshUser() re-lee /api/user/me/ → tos_needs_acceptance pasa a false → el
// gate se desmonta solo y deja pasar.
//
// ORDEN vs ProfileCompletionGate: este gate va PRIMERO (montado antes en el
// AppShell). Aceptar los términos —que incluyen el marco de tratamiento de datos
// (Ley 21.719) bajo el cual el usuario luego sube su carnet + certificado en el
// VerificationWizard— es condición previa a completar el perfil. Como este gate
// renderiza un overlay bloqueante, tapa al ProfileCompletionGate hasta que se
// acepta; recién ahí ProfileCompletionGate toma el control y rutea al wizard.

import { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Link,
  Typography,
} from "@mui/material";
import GavelOutlinedIcon from "@mui/icons-material/GavelOutlined";
import { useCurrentUser } from "../context/UserContext";
import RichMarkdown from "../markdown/RichMarkdownDiferido"; // #1717 R12: el motor de markdown baja al mostrar los Términos
import getClient from "../../helpers/get-client";
import { tosMarkdownFor, tosVersionFor } from "../../content/tosPorUsuario";
import AvisoWhatsapp from "../comunicaciones/AvisoWhatsapp";

const client = getClient();

// Qué texto acepta cada uno vive en content/tosPorUsuario.js: es la MISMA fuente
// que renderiza la página pública /terms-and-conditions, a la que enlaza este gate
// (revisor externo, 02-oct-2026, NB1: antes eran dos textos distintos y el enlace
// llevaba a uno que contradecía lo que el médico estaba aceptando). Un interno o un
// estudiante (#1738 R10) no acepta el mismo documento que un médico habilitado: el
// backend exige la versión que le toca (user_api/tos.py) y guarda ESA versión.

// Firman TODOS, superusers incluidos. El admin veía la app sin haber firmado
// nunca el documento que sus propios usuarios sí firman. Si acá se vuelve a
// eximir a alguien, hay que eximirlo TAMBIÉN en el backend
// (`user_api/tos.py:needs_tos_acceptance`): exento en un solo lado = la API lo
// bloquea con 403 y no hay pantalla para aceptar.
const needsTosAcceptance = (user) => {
  if (!user) return false;
  return Boolean(user.tos_needs_acceptance);
};

const TosAcceptanceGate = () => {
  const { currentUser, loading, refreshUser } = useCurrentUser();
  const [checked, setChecked] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  // El servidor exige los Términos en todo /api/ (backend `app/tos_guard.py`)
  // y contesta 403 `tos_not_accepted`. Si llega ese 403, el /me que tenemos
  // está viejo (se publicó otra versión con la sesión abierta, o un bundle
  // cacheado se saltó el gate): se muestra el overlay igual y se re-lee /me.
  const [exigidoPorServidor, setExigidoPorServidor] = useState(false);
  // Las pantallas se montan DEBAJO del overlay y, sin Términos, el servidor les
  // contesta 403: quedan con errores y datos vacíos ("No pudimos cargar las
  // pestanas"). Si hubo al menos un bloqueo, al aceptar se recarga la app para
  // que todo vuelva a pedir sus datos ya firmado.
  const huboBloqueo = useRef(false);
  const yaRefrescado = useRef(false);
  // Ronda 2 (revisor): el overlay convive con pantallas que quedaron montadas
  // DEBAJO (Turno.js sigue reintentando tabs/lugares/etc mientras el usuario
  // lee los Términos) y CADA una de esas llamadas de fondo también contesta
  // 403 tos_not_accepted mientras no se acepta. Antes, `onBloqueo` hacía
  // `setChecked(false)` en CADA evento, así que el primer 403 de fondo que
  // llegaba justo después del click del usuario le borraba la casilla sin que
  // hubiera tocado nada: el gate se reabría solo, una y otra vez, y el click
  // nunca alcanzaba a sobrevivir. Esta marca evita repetir el reset de
  // checked/submitting mientras YA estamos mostrando el overlay por este
  // motivo: solo se limpia la primera vez que entra en este estado.
  const bloqueadoRef = useRef(false);

  useEffect(() => {
    const onBloqueo = (event) => {
      if (event?.detail?.code !== "tos_not_accepted") return;
      huboBloqueo.current = true;
      if (!bloqueadoRef.current) {
        bloqueadoRef.current = true;
        // Si el overlay vuelve a aparecer en la misma sesión, arranca limpio:
        // sin esto quedaba en "Registrando..." con la casilla deshabilitada.
        setSubmitting(false);
        setChecked(false);
        setExigidoPorServidor(true);
      }
      // Una sola relectura de /me por racha: con el overlay arriba, el polling
      // de fondo (mic/request/ en el celular) dispara decenas de 403.
      if (yaRefrescado.current) return;
      yaRefrescado.current = true;
      Promise.resolve(refreshUser?.()).catch(() => {});
    };
    window.addEventListener("smartdx:acceso-bloqueado", onBloqueo);
    return () => window.removeEventListener("smartdx:acceso-bloqueado", onBloqueo);
  }, [refreshUser]);

  const show =
    !loading &&
    Boolean(currentUser) &&
    (needsTosAcceptance(currentUser) || exigidoPorServidor);

  // Bloquea el scroll del fondo mientras el overlay está montado.
  useEffect(() => {
    if (!show) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [show]);

  if (!show) return null;

  const version = tosVersionFor(currentUser);
  const markdown = tosMarkdownFor(currentUser, version);

  const handleAccept = async () => {
    setSubmitting(true);
    setError(null);
    try {
      // Cubre R12 — .claude/specs/1770b-clausula-mejora-terminos/
      // Se declara la versión del texto que se MOSTRÓ (la misma `version` de arriba): el back solo registra
      // la v2.1 si coincide, así una pestaña con el bundle viejo no deja una aceptación sin haber visto la 3 ter.
      await client.post("/api/user/accept-tos/", { version });
      if (huboBloqueo.current) {
        window.location.reload();
        return;
      }
      // Re-lee el user; al volver con tos_needs_acceptance=false el gate se
      // desmonta solo. El reset va DESPUÉS del refresh (el overlay ya no se
      // pinta, no hay flash) para que un bloqueo posterior lo encuentre limpio.
      await refreshUser();
      yaRefrescado.current = false;
      setExigidoPorServidor(false);
      setSubmitting(false);
      setChecked(false);
    } catch (e) {
      // Cubre R13 — .claude/specs/1770b-clausula-mejora-terminos/
      // 409 `tos_version_desfasada`: el texto que se mostró no es el que exige el servidor (los Términos
      // cambiaron con el gate abierto). Se muestra el error de siempre (la aceptación NO quedó registrada),
      // se relee /me para traer la versión nueva y se destraba la casilla. El overlay sigue: sin `reload`.
      // Si la relectura falla, el error ya está en pantalla y se puede reintentar.
      const desfasada = e?.response?.status === 409 && e?.response?.data?.code === "tos_version_desfasada";
      setError(
        "No pudimos registrar tu aceptación. Revisa tu conexión y reintenta."
      );
      if (desfasada) {
        try {
          await refreshUser();
        } catch (_) {
          /* el error ya se ve */
        }
        setChecked(false);
      }
      setSubmitting(false);
    }
  };

  return (
    <Box
      role="dialog"
      aria-modal="true"
      aria-label="Términos de Uso"
      sx={{
        position: "fixed",
        inset: 0,
        zIndex: 20000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: { xs: 0, sm: 2 },
        // Pantalla completa propia en el celular: suma la barra de estado del iPhone (26-oct-2026, #1741).
        pt: { xs: "env(safe-area-inset-top)", sm: 2 },
        backgroundColor: "rgba(var(--shadow-rgb), 0.72)",
        backdropFilter: "blur(4px)",
      }}
    >
      <Box
        sx={{
          width: "100%",
          maxWidth: 720,
          height: { xs: "100%", sm: "auto" },
          maxHeight: { xs: "100%", sm: "90vh" },
          display: "flex",
          flexDirection: "column",
          backgroundColor: "var(--bg-elevated)",
          border: "1px solid rgba(var(--fg-rgb), 0.1)",
          borderRadius: { xs: 0, sm: "16px" },
          boxShadow: "0 24px 64px rgba(var(--shadow-rgb), 0.5)",
          overflow: "hidden",
        }}
      >
        {/* Header */}
        <Box
          sx={{
            flex: "0 0 auto",
            display: "flex",
            alignItems: "center",
            gap: 1.25,
            px: { xs: 2.25, sm: 3 },
            py: { xs: 2, sm: 2.25 },
            borderBottom: "1px solid rgba(var(--fg-rgb), 0.08)",
          }}
        >
          <Box
            sx={{
              flex: "0 0 auto",
              width: 40,
              height: 40,
              borderRadius: "12px",
              display: "grid",
              placeItems: "center",
              color: "var(--color-primary-soft)",
              backgroundColor: "rgba(var(--color-primary-rgb), 0.14)",
              border: "1px solid rgba(var(--color-primary-rgb), 0.35)",
            }}
          >
            <GavelOutlinedIcon sx={{ fontSize: 22 }} />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography
              component="h2"
              sx={{
                fontSize: { xs: "1.15rem", sm: "1.3rem" },
                fontWeight: 700,
                lineHeight: 1.2,
                color: "var(--text-primary)",
              }}
            >
              Términos de Uso
            </Typography>
            <Typography
              sx={{
                fontSize: "0.8rem",
                color: "var(--text-muted)",
                mt: 0.25,
              }}
            >
              Para continuar, lee y acepta los Términos de Uso de SmartDx.
            </Typography>
          </Box>
        </Box>

        {/* Cuerpo scrolleable con el ToS renderizado desde el markdown */}
        <Box
          sx={{
            flex: "1 1 auto",
            overflowY: "auto",
            overflowX: "hidden",
            px: { xs: 2.25, sm: 3 },
            py: { xs: 2, sm: 2.5 },
            // Separadores del markdown con token (evita el <hr> gris del browser).
            "& hr": {
              border: "none",
              borderTop: "1px solid rgba(var(--fg-rgb), 0.1)",
              my: 2,
            },
          }}
        >
          <RichMarkdown headingBars={false}>{markdown}</RichMarkdown>
        </Box>

        {/* Footer: checkbox + acción */}
        <Box
          sx={{
            flex: "0 0 auto",
            px: { xs: 2.25, sm: 3 },
            py: { xs: 2, sm: 2.25 },
            borderTop: "1px solid rgba(var(--fg-rgb), 0.08)",
            backgroundColor: "rgba(var(--fg-rgb), 0.02)",
          }}
        >
          <FormControlLabel
            control={
              <Checkbox
                checked={checked}
                onChange={(e) => setChecked(e.target.checked)}
                disabled={submitting}
                sx={{
                  color: "rgba(var(--fg-rgb), 0.4)",
                  "&.Mui-checked": { color: "var(--color-primary)" },
                }}
              />
            }
            label={
              <Typography
                sx={{ fontSize: "0.92rem", color: "var(--text-secondary)" }}
              >
                He leído y acepto los Términos de Uso
              </Typography>
            }
            sx={{ mr: 0, alignItems: "center", minHeight: 44 }}
          />

          {/* #1683 v2 (R65): aceptar los Términos también da el permiso de WhatsApp (D3 v2); el aviso
              lo dice sin casilla y sigue siendo verdadero para quien ya se dio de baja. */}
          <AvisoWhatsapp testid="tos-aviso-whatsapp" lugar="terminos" sx={{ mb: 1 }} />

          {error && (
            <Typography
              sx={{
                mt: 0.5,
                mb: 1,
                fontSize: "0.85rem",
                color: "var(--color-danger)",
              }}
            >
              {error}
            </Typography>
          )}

          <Box
            sx={{
              mt: 1.5,
              display: "flex",
              flexDirection: { xs: "column-reverse", sm: "row" },
              alignItems: { xs: "stretch", sm: "center" },
              justifyContent: "space-between",
              gap: 1.25,
            }}
          >
            <Link
              href="/terms-and-conditions"
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                // R60 (revisor v7 B11): objetivo táctil de 44 px, también en el link del pie.
                display: "inline-flex",
                alignItems: "center",
                justifyContent: { xs: "center", sm: "flex-start" },
                minHeight: 44,
                fontSize: "0.85rem",
                color: "var(--color-primary-soft)",
                textDecorationColor: "rgba(var(--color-primary-rgb), 0.5)",
                textAlign: { xs: "center", sm: "left" },
                fontWeight: 500,
              }}
            >
              Ver términos en una pestaña nueva
            </Link>

            <Button
              onClick={handleAccept}
              disabled={!checked || submitting}
              variant="contained"
              disableElevation
              startIcon={
                submitting ? (
                  <CircularProgress size={16} sx={{ color: "inherit" }} />
                ) : null
              }
              sx={{
                textTransform: "none",
                fontWeight: 600,
                px: 3,
                py: 1,
                borderRadius: "10px",
                color: "var(--text-on-primary)",
                backgroundColor: "var(--color-primary)",
                "&:hover": { backgroundColor: "var(--color-primary-soft)" },
                "&.Mui-disabled": {
                  color: "rgba(var(--fg-rgb), 0.4)",
                  backgroundColor: "rgba(var(--fg-rgb), 0.1)",
                },
              }}
            >
              {submitting ? "Registrando..." : "Aceptar y continuar"}
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default TosAcceptanceGate;
